import { IPublicClientApplication } from '@azure/msal-browser';
import { Nomination, NominationPartnerType, NominationStatus } from '../../models';
import { ApiEndpointCallStatesStore } from '../api-endpoint-call-states-store';
import { FetchContext } from '../fetch-context';
import { promiseWithObservableState } from '../promise-with-observable-state';
import { withApiAccessToken } from '../with-api-access-token';
import {
  createNomination,
  deleteNomination,
  getAwardedNominations,
  getNominationById,
  getNominationForStaff,
  getNominationPermissions,
  getNominationSummaries,
  updateNomination,
  updateNominationAbstract,
  updateNominationCountries,
  updateNominationIsCaseStudy,
  updateNominationMarketplaceOffer,
  updateNominationPartnerTypes,
  updateNominationStatus,
} from './index';

export const createNominationsApiEndpoints = (
  fetchContext: FetchContext,
  apiEndpointCallStates: ApiEndpointCallStatesStore,
) => ({
  getNominationById: (instance: IPublicClientApplication, nominationId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getNominationById(fetchContext, nominationId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getNominationById', isInFlight);
    },
  ),
  getNominationPermissions: (instance: IPublicClientApplication, nominationId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getNominationPermissions(fetchContext, nominationId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getNominationPermissions', isInFlight);
    },
  ),
  getNominationForStaff: (instance: IPublicClientApplication, nominationId: number, countryId?: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getNominationForStaff(fetchContext, nominationId, countryId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getNominationForStaff', isInFlight);
    },
  ),
  updateNominationCountries: (instance: IPublicClientApplication, nominationId: number, countryIds: number[]) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateNominationCountries(fetchContext, nominationId, countryIds, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateNominationCountries', isInFlight);
    },
  ),
  getNominationSummaries: (instance: IPublicClientApplication) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getNominationSummaries(fetchContext, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getNominationSummaries', isInFlight);
    },
  ),
  getAwardedNominations: (instance: IPublicClientApplication) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardedNominations(fetchContext, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardedNominations', isInFlight);
    },
  ),
  createNomination: (instance: IPublicClientApplication, nomination: Nomination) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createNomination(fetchContext, nomination, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createNomination', isInFlight);
    },
  ),
  updateNomination: (instance: IPublicClientApplication, nomination: Nomination) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateNomination(fetchContext, nomination, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateNomination', isInFlight);
    },
  ),
  deleteNomination: (instance: IPublicClientApplication, nominationId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteNomination(fetchContext, nominationId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteNomination', isInFlight);
    },
  ),
  updateNominationIsCaseStudy: (instance: IPublicClientApplication, nominationId: number, isCaseStudy: boolean, countryId?: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateNominationIsCaseStudy(fetchContext, nominationId, isCaseStudy, countryId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateNominationIsCaseStudy', isInFlight);
    },
  ),
  updateNominationStatus: (instance: IPublicClientApplication, nominationId: number, nominationStatus: NominationStatus) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateNominationStatus(fetchContext, nominationId, nominationStatus, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateNominationStatus', isInFlight);
    },
  ),
  updateNominationAbstract: (instance: IPublicClientApplication, nominationId: number, abstract: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateNominationAbstract(fetchContext, nominationId, abstract, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateNominationAbstract', isInFlight);
    },
  ),
  updateNominationMarketplaceOffer: (instance: IPublicClientApplication, nominationId: number, marketplaceOfferUrl?: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateNominationMarketplaceOffer(fetchContext, nominationId, marketplaceOfferUrl, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateNominationMarketplaceOffer', isInFlight);
    },
  ),
  updateNominationPartnerTypes: (instance: IPublicClientApplication, nominationId: number, partnerTypes: NominationPartnerType[]) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateNominationPartnerTypes(fetchContext, nominationId, partnerTypes, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateNominationPartnerTypes', isInFlight);
    },
  ),
});
